import qz from "qz-tray";

const DEFAULT_PRINTER_NAME = "Xprinter XP-420B";

const normalizePrinterName = (name) => String(name || "").trim().toLowerCase();

const matchesLabelPrinter = (availableName, wantedName) => {
  const available = normalizePrinterName(availableName);
  const wanted = normalizePrinterName(wantedName);
  if (!available || !wanted) return false;
  if (available === wanted) return true;
  // Windows duplicate drivers: "Xprinter XP-420B (Copy 1)"
  return available.startsWith(wanted) || wanted.startsWith(available);
};

export const ensureQzConnected = async () => {
  if (qz.websocket.isActive()) return;
  await qz.websocket.connect();
};

/**
 * Resolve the thermal label printer even if Windows renamed it
 * (e.g. "Xprinter XP-420B (Copy 1)").
 */
export const resolveLabelPrinterName = async (
  preferredName = DEFAULT_PRINTER_NAME
) => {
  await ensureQzConnected();
  const found = await qz.printers.find();
  const list = (Array.isArray(found) ? found : found ? [found] : []).map(String);
  const wanted = String(preferredName || DEFAULT_PRINTER_NAME).trim();

  const exact = list.find(
    (printer) => normalizePrinterName(printer) === normalizePrinterName(wanted)
  );
  if (exact) return exact;

  const fuzzy = list.find((printer) => matchesLabelPrinter(printer, wanted));
  if (fuzzy) return fuzzy;

  if (normalizePrinterName(wanted) !== normalizePrinterName(DEFAULT_PRINTER_NAME)) {
    const baseExact = list.find(
      (printer) =>
        normalizePrinterName(printer) === normalizePrinterName(DEFAULT_PRINTER_NAME)
    );
    if (baseExact) return baseExact;
    const baseFuzzy = list.find((printer) =>
      matchesLabelPrinter(printer, DEFAULT_PRINTER_NAME)
    );
    if (baseFuzzy) return baseFuzzy;
  }

  throw new Error(
    `لم يتم العثور على طابعة الملصقات "${wanted}". الطابعات المتاحة: ${
      list.length ? list.join("، ") : "لا يوجد"
    }`
  );
};

export const createLabelPrinterConfig = async (printerName = DEFAULT_PRINTER_NAME) => {
  const resolved = await resolveLabelPrinterName(printerName);
  console.log("[QZ] Using printer:", resolved);
  return qz.configs.create(resolved);
};

/**
 * Minimal RAW print test — verifies QZ Tray connect + print flow.
 * No HTML, no canvas. One raw job to the label printer.
 */
export const printRawTest = async (printerName = DEFAULT_PRINTER_NAME) => {
  console.log("[QZ] Before connect");

  try {
    await ensureQzConnected();
    console.log("[QZ] After connect — isActive:", qz.websocket.isActive());
  } catch (connectErr) {
    console.error("[QZ] Connect failed:", connectErr);
    throw new Error(
      connectErr?.message ||
        "Failed to connect to QZ Tray. Make sure QZ Tray is running."
    );
  }

  try {
    const config = await createLabelPrinterConfig(printerName);

    await qz.print(config, [
      {
        type: "raw",
        data: "HELLO\nHELLO\n",
      },
    ]);

    console.log("[QZ] After print — job sent successfully");
  } catch (printErr) {
    console.error("[QZ] Print failed:", printErr);
    throw new Error(
      printErr?.message || "Print job failed. Check printer name and QZ Tray."
    );
  }
};

/**
 * Print a PDF (e.g. Fincart thermal AWB) on the label printer via QZ Tray.
 */
export const printPdfBase64 = async (
  base64Pdf,
  printerName = DEFAULT_PRINTER_NAME
) => {
  try {
    await ensureQzConnected();
  } catch (connectErr) {
    throw new Error(
      connectErr?.message ||
        "فشل الاتصال بـ QZ Tray. تأكد أن البرنامج شغال."
    );
  }

  const data = String(base64Pdf || "").replace(
    /^data:application\/pdf;base64,/i,
    ""
  );

  if (!data) {
    throw new Error("ملف البوليصة فارغ");
  }

  try {
    const config = await createLabelPrinterConfig(printerName);
    await qz.print(config, [
      {
        type: "pixel",
        format: "pdf",
        flavor: "base64",
        data,
      },
    ]);
  } catch (printErr) {
    console.error("[QZ] PDF print failed:", printErr);
    throw new Error(
      printErr?.message || "فشل طباعة البوليصة. تحقق من الطابعة و QZ Tray."
    );
  }
};

/**
 * Print phone type name(s) as simple thermal labels (name only) on the small printer.
 * @param {string|string[]} names - one name or list of names (one label each)
 */
export const printTypeNameLabels = async (
  names,
  printerName = DEFAULT_PRINTER_NAME
) => {
  const list = (Array.isArray(names) ? names : [names])
    .map((n) => String(n || "").trim())
    .filter(Boolean);

  if (list.length === 0) {
    throw new Error("لا توجد أسماء للطباعة");
  }

  try {
    await ensureQzConnected();
  } catch (connectErr) {
    throw new Error(
      connectErr?.message ||
        "فشل الاتصال بـ QZ Tray. تأكد أن البرنامج شغال."
    );
  }

  // 50×25 mm @ ~8 dots/mm ≈ 400×200 dots
  // Font "3" ≈ 16×24 dots/char @ 1×1 → ~22 chars/line; wrap early so nothing clips
  const maxPerLine = 18;

  const wrapName = (name) => {
    const safe = name.replace(/"/g, "'").trim();
    if (safe.length <= maxPerLine) return [safe];

    const mid = Math.ceil(safe.length / 2);
    let best = -1;
    for (let i = 1; i < safe.length - 1; i++) {
      if (safe[i] !== " ") continue;
      const left = safe.slice(0, i).trim();
      const right = safe.slice(i + 1).trim();
      if (left.length === 0 || right.length === 0) continue;
      if (left.length > maxPerLine || right.length > maxPerLine) continue;
      if (best === -1 || Math.abs(i - mid) < Math.abs(best - mid)) {
        best = i;
      }
    }

    if (best !== -1) {
      return [safe.slice(0, best).trim(), safe.slice(best + 1).trim()];
    }

    // No suitable space — hard cut at maxPerLine (second line capped too)
    return [
      safe.slice(0, maxPerLine).trim(),
      safe.slice(maxPerLine).trim().slice(0, maxPerLine),
    ].filter(Boolean);
  };

  const buildTspl = (name) => {
    const lines = wrapName(name);
    const lineH = 40;
    const startY = lines.length === 1 ? 75 : 50;
    const textCmds = lines
      .map((line, i) => `TEXT 45,${startY + i * lineH},"3",0,1,1,"${line}"`)
      .join("\n");

    return `
SIZE 50 mm,25 mm
GAP 2 mm,0 mm
CLS
${textCmds}
PRINT 1
`;
  };

  try {
    const config = await createLabelPrinterConfig(printerName);
    const data = list.map((name) => ({ type: "raw", data: buildTspl(name) }));
    await qz.print(config, data);
    return data.length;
  } catch (printErr) {
    console.error("[QZ] Type name print failed:", printErr);
    throw new Error(
      printErr?.message || "فشل الطباعة. تحقق من الطابعة و QZ Tray."
    );
  }
};

/**
 * Print small 50×25 mm labels: order number + piece count.
 * @param {{ orderNumber: string|number, pieces: number }[]} orders
 */
export const printOrderPieceLabels = async (
  orders,
  printerName = DEFAULT_PRINTER_NAME
) => {
  const list = (Array.isArray(orders) ? orders : [])
    .map((o) => ({
      orderNumber: String(o?.orderNumber ?? o?.orderId ?? "").trim(),
      pieces: Math.max(0, Number(o?.pieces ?? o?.totalItemsInOrder ?? 0) || 0),
    }))
    .filter((o) => o.orderNumber);

  if (list.length === 0) {
    throw new Error("لا توجد طلبات للطباعة");
  }

  try {
    await ensureQzConnected();
  } catch (connectErr) {
    throw new Error(
      connectErr?.message ||
        "فشل الاتصال بـ QZ Tray. تأكد أن البرنامج شغال."
    );
  }

  const buildTspl = ({ orderNumber, pieces }) => {
    const safeOrder = orderNumber.replace(/"/g, "'");
    const piecesLine = `${pieces} pcs`;
    return `
SIZE 50 mm,25 mm
GAP 2 mm,0 mm
CLS
TEXT 30,45,"3",0,2,2,"#${safeOrder}"
TEXT 30,115,"3",0,1,1,"${piecesLine}"
PRINT 1
`;
  };

  try {
    const config = await createLabelPrinterConfig(printerName);
    const data = list.map((o) => ({ type: "raw", data: buildTspl(o) }));
    await qz.print(config, data);
    return data.length;
  } catch (printErr) {
    console.error("[QZ] Order piece label print failed:", printErr);
    throw new Error(
      printErr?.message || "فشل الطباعة. تحقق من الطابعة و QZ Tray."
    );
  }
};
